/**
 * Warband member image service.
 *
 * Two storage modes:
 *
 *   Local (not logged in to Drive):
 *     Images are stored as Blobs in IndexedDB via imageDbService.
 *
 *   Drive (logged in):
 *     When a user selects a gallery image via assignDriveImage(), the image is
 *     downloaded from Drive and cached in IndexedDB so it is available offline.
 *     The Drive file ID is written to StoredMember.driveImageId so it travels
 *     with warband data when it syncs to other devices.
 *
 *     Local uploads are recorded as pending and published to the gallery by the
 *     next synchronization. The pending record remains until the warband JSON
 *     containing the new Drive file ID is uploaded successfully.
 *
 *     fetchImage() lazily downloads from Drive when IndexedDB has no image but
 *     StoredMember.driveImageId is set — this is how images reach new devices
 *     after a warband sync.
 */

import { imageDbService } from './imageDbService';
import { useAuthStore } from '../stores/authStore';
import {
  findOrCreateGalleryFolder,
  uploadImageToDrive,
  downloadImageFromDrive,
} from './googleDriveImageService';
import { findOrCreateFolder } from './googleDriveService';
import {
  deletePendingMemberImage,
  getMemberDriveImageId,
  getPendingMemberImage,
  getPendingMemberImages,
  loadWarbandData,
  PendingMemberImage,
  savePendingMemberImage,
  StoredMember,
  StoredWarbandData,
  updateMemberDriveImageId,
} from './localStorageService';

const DRIVE_REF_PREFIX = 'frostgrave:drive-image-ref:';

function driveRefKey(warbandId: string, memberId: string): string {
  return `${DRIVE_REF_PREFIX}${warbandId}:${memberId}`;
}

/** Keys currently being downloaded from Drive to prevent duplicate in-flight requests. */
const downloadInProgress = new Set<string>();

function queuePortraitSync(warbandId: string, memberId: string): void {
  void import('./syncCoordinator').then(({ queueSyncEntity }) => {
    queueSyncEntity(`portrait:${warbandId}:${memberId}`);
  });
}

export interface MemberImageConflict {
  warbandId: string;
  memberId: string;
  memberName: string;
  remoteDriveImageId: string | null;
}

function findMember(data: StoredWarbandData | null, memberId: string): StoredMember | null {
  if (!data) return null;
  if (data.wizard?.id === memberId) return data.wizard;
  if (data.apprentice?.id === memberId) return data.apprentice;
  const frostgraveSoldier = data.soldiers.find((soldier) => soldier.id === memberId);
  if (frostgraveSoldier) return frostgraveSoldier;
  const stargraveMember = data.stargrave?.captain?.id === memberId
    ? data.stargrave.captain
    : data.stargrave?.firstMate?.id === memberId
      ? data.stargrave.firstMate
      : data.stargrave?.soldiers.find((soldier) => soldier.id === memberId);
  return stargraveMember ? { id: stargraveMember.id, creatureId: 'stargrave', name: stargraveMember.name, currentHealth: stargraveMember.gameState.currentHealth, notes: null, isTemporary: false, weaponIds: [], armourIds: [], buffIds: [], treasureItemIds: [], storedInInn: 0, leftTable: stargraveMember.gameState.leftTable, carryingTreasure: false, driveImageId: stargraveMember.driveImageId } : null;
}

async function preparePendingImage(
  pending: PendingMemberImage,
  remote: StoredWarbandData | null,
  accessToken: string,
  forceLocal: boolean
): Promise<MemberImageConflict | null> {
  const remoteDriveImageId = findMember(remote, pending.memberId)?.driveImageId ?? null;
  if (
    !forceLocal &&
    pending.baseDriveImageId !== remoteDriveImageId &&
    pending.selectedDriveImageId !== remoteDriveImageId
  ) {
    const local = findMember(loadWarbandData(pending.warbandId), pending.memberId);
    return {
      warbandId: pending.warbandId,
      memberId: pending.memberId,
      memberName: local?.name ?? 'Warband member',
      remoteDriveImageId,
    };
  }

  let selectedDriveImageId = pending.selectedDriveImageId;
  const currentDriveImageId = getMemberDriveImageId(pending.warbandId, pending.memberId);
  if (
    !selectedDriveImageId &&
    currentDriveImageId &&
    currentDriveImageId !== pending.baseDriveImageId
  ) {
    selectedDriveImageId = currentDriveImageId;
    savePendingMemberImage({ ...pending, selectedDriveImageId });
  }
  if (!selectedDriveImageId) {
    const blob = await imageDbService.loadImage(pending.warbandId, pending.memberId);
    if (!blob) throw new Error(`Pending portrait is missing for ${pending.memberId}`);
    const parentFolderId = await findOrCreateFolder(accessToken);
    const galleryFolderId = await findOrCreateGalleryFolder(accessToken, parentFolderId);
    selectedDriveImageId = await uploadImageToDrive(
      accessToken,
      galleryFolderId,
      `member-${pending.memberId}.png`,
      blob
    );
    savePendingMemberImage({ ...pending, selectedDriveImageId });
  }

  localStorage.setItem(
    driveRefKey(pending.warbandId, pending.memberId),
    selectedDriveImageId
  );
  updateMemberDriveImageId(pending.warbandId, pending.memberId, selectedDriveImageId);
  return null;
}

export const warbandMemberImageService = {
  /**
   * Save an image (already resized by ImageUploader) for a warband member.
   *
  * Always writes to IndexedDB and records a durable pending selection. Drive
  * publication is handled by synchronization so failures can be retried.
   */
  uploadImage: async (warbandId: string, memberId: string, file: File): Promise<void> => {
    const baseDriveImageId = getMemberDriveImageId(warbandId, memberId);
    await imageDbService.saveImage(warbandId, memberId, file);
    localStorage.removeItem(driveRefKey(warbandId, memberId));
    savePendingMemberImage({
      warbandId,
      memberId,
      source: 'local-upload',
      baseDriveImageId,
    });
    queuePortraitSync(warbandId, memberId);
  },

  /**
   * Assign a Drive gallery image to a warband member.
   *
   * Downloads the image from Drive, caches it in IndexedDB (so it works
   * offline), and stores the Drive file ID for reference.
   */
  assignDriveImage: async (
    warbandId: string,
    memberId: string,
    driveFileId: string,
    accessToken: string
  ): Promise<void> => {
    const blob = await downloadImageFromDrive(accessToken, driveFileId);
    await imageDbService.saveImage(warbandId, memberId, blob);
    savePendingMemberImage({
      warbandId,
      memberId,
      source: 'drive-selection',
      baseDriveImageId: getMemberDriveImageId(warbandId, memberId),
      selectedDriveImageId: driveFileId,
    });
    localStorage.setItem(driveRefKey(warbandId, memberId), driveFileId);
    updateMemberDriveImageId(warbandId, memberId, driveFileId);
    queuePortraitSync(warbandId, memberId);
  },

  hasPendingImage: (warbandId: string, memberId: string): boolean =>
    getPendingMemberImage(warbandId, memberId) !== null,

  preparePendingImages: async (
    warbandId: string,
    remote: StoredWarbandData | null,
    accessToken: string,
    forceLocal = false
  ): Promise<MemberImageConflict[]> => {
    const conflicts: MemberImageConflict[] = [];
    for (const pending of getPendingMemberImages(warbandId)) {
      const conflict = await preparePendingImage(pending, remote, accessToken, forceLocal);
      if (conflict) conflicts.push(conflict);
    }
    return conflicts;
  },

  completePendingImages: (warbandId: string): void => {
    for (const pending of getPendingMemberImages(warbandId)) {
      deletePendingMemberImage(pending.warbandId, pending.memberId);
    }
  },

  resolveConflictWithLocal: async (
    conflict: MemberImageConflict,
    accessToken: string
  ): Promise<void> => {
    const pending = getPendingMemberImage(conflict.warbandId, conflict.memberId);
    if (!pending) return;
    savePendingMemberImage({ ...pending, baseDriveImageId: conflict.remoteDriveImageId });
    await preparePendingImage(
      { ...pending, baseDriveImageId: conflict.remoteDriveImageId },
      null,
      accessToken,
      true
    );
  },

  resolveConflictWithDrive: async (
    conflict: MemberImageConflict,
    accessToken: string
  ): Promise<void> => {
    if (conflict.remoteDriveImageId) {
      const blob = await downloadImageFromDrive(accessToken, conflict.remoteDriveImageId);
      await imageDbService.saveImage(conflict.warbandId, conflict.memberId, blob);
      localStorage.setItem(
        driveRefKey(conflict.warbandId, conflict.memberId),
        conflict.remoteDriveImageId
      );
    } else {
      await imageDbService.deleteImage(conflict.warbandId, conflict.memberId);
      localStorage.removeItem(driveRefKey(conflict.warbandId, conflict.memberId));
    }
    updateMemberDriveImageId(
      conflict.warbandId,
      conflict.memberId,
      conflict.remoteDriveImageId
    );
    deletePendingMemberImage(conflict.warbandId, conflict.memberId);
  },

  discardPendingImages: async (
    warbandId: string,
    remote: StoredWarbandData | null,
    accessToken: string
  ): Promise<void> => {
    for (const pending of getPendingMemberImages(warbandId)) {
      const remoteDriveImageId = findMember(remote, pending.memberId)?.driveImageId ?? null;
      const local = findMember(loadWarbandData(warbandId), pending.memberId);
      await warbandMemberImageService.resolveConflictWithDrive({
        warbandId,
        memberId: pending.memberId,
        memberName: local?.name ?? 'Warband member',
        remoteDriveImageId,
      }, accessToken);
    }
  },

  /**
   * Retrieve the image for a warband member.
   *
  * Fast path: return a pending local Blob or a cache matching driveImageId.
  * Sync path: download and replace a missing or stale cache from Drive.
   */
  fetchImage: async (warbandId: string, memberId: string): Promise<Blob | null> => {
    const cached = await imageDbService.loadImage(warbandId, memberId);
    if (getPendingMemberImage(warbandId, memberId)) return cached;

    const driveImageId = getMemberDriveImageId(warbandId, memberId);
    if (!driveImageId) return cached;

    const cachedDriveImageId = localStorage.getItem(driveRefKey(warbandId, memberId));
    if (cached && cachedDriveImageId === driveImageId) return cached;

    const { accessToken } = useAuthStore.getState();
    if (!accessToken) return null;

    const key = `${warbandId}:${memberId}`;
    if (downloadInProgress.has(key)) return null;

    downloadInProgress.add(key);
    try {
      const blob = await downloadImageFromDrive(accessToken, driveImageId);
      await imageDbService.saveImage(warbandId, memberId, blob);
      localStorage.setItem(driveRefKey(warbandId, memberId), driveImageId);
      return blob;
    } catch {
      return null;
    } finally {
      downloadInProgress.delete(key);
    }
  },
};
